import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import {
  HiArrowLeft,
  HiOutlineExclamationCircle,
  HiOutlineShieldCheck,
  HiSparkles,
} from "react-icons/hi";
import { useQuery } from "react-query";
import axiosInstance from "../config/axiosInstance";
import { ADMIN, USERS } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import ForbiddenQueriesPermission from "../components/common/ForbiddenQueriesPermission";
import "./CreditEngineSettingsPage.css";

const cx = (...values) => values.filter(Boolean).join(" ");

const moneyFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const DEFAULT_FORM_STATE = {
  autoReject: {
    protesto: false,
    protestoMinValue: "",
    chequeSemFundo: false,
    chequeMinCount: "",
    pendenciaFinanceira: false,
    pendenciaMinValue: "",
  },
  scoreThreshold: "",
  foundationMinAgeMonths: "",
  recency: {
    protesto: "",
    chequeSemFundo: "",
    pendenciaFinanceira: "",
    acaoCivil: "",
    recuperacaoJudicial: "",
    processosLesivos: "",
  },
  notes: "",
  advanced: {
    behavioral: {
      active: false,
      minScore: "",
    },
    location: {
      active: false,
      mode: "blocked",
      states: "",
    },
    companySize: {
      active: false,
      porte: "",
    },
    creditLimit: {
      active: false,
      minValue: "",
    },
    faturamento: {
      active: false,
      minValue: "",
    },
    age: {
      active: false,
      minAge: "",
      maxAge: "",
    },
  },
  legal: {
    acaoCivil: false,
    recuperacaoJudicial: false,
    processosLesivos: false,
    processosImageUrl: "",
  },
};

const currencyPattern = /^[0-9]+([.,][0-9]{0,2})?$/;
const digitsPattern = /^[0-9]*$/;
const normalizeQueryId = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const toNumber = (raw) => {
  if (raw === "" || raw == null) return null;
  const normalized = String(raw)
    .replace(/[R$\s.]/g, "")
    .replace(",", ".");
  const n = Number(normalized);
  return Number.isNaN(n) ? null : n;
};

const formatCurrencyInput = (value) => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  const num = Number(digits) / 100;
  return moneyFmt.format(num);
};

const formatDateForDisplay = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR");
};

const formatCurrency = (value) => {
  const num = toNumber(value);
  if (num == null) return null;
  return moneyFmt.format(num);
};

const safeCurrency = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number") return moneyFmt.format(value);
  const parsed = toNumber(value);
  return parsed == null ? "" : moneyFmt.format(parsed);
};

const RECENCY_OPTIONS = [
  { value: "", label: "Sem recência" },
  { value: "veryRecent", label: "Muito recente (<1 mês)" },
  { value: "recent", label: "Recente (1-3 meses)" },
  { value: "moderate", label: "Intermediário (3-6 meses)" },
  { value: "old", label: "Antigo (>6 meses)" },
];

const RECENCY_LABELS = RECENCY_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
);
const AGE_RANGE_MAX = 120;

const describeRecency = (value) =>
  value ? RECENCY_LABELS[value] || value : "Sem filtro de recência";
const appendRecency = (text, value) =>
  value ? `${text} (${describeRecency(value)})` : text;
const recencyPhrase = (value) => `Recência: ${describeRecency(value)}.`;
const boldValue = (value) => (
  <strong className="ces-summary-strong">{value}</strong>
);
const appendRecencyNode = (textNode, value) => (
  <>
    {textNode}
    {value ? (
      <>
        {" "}
        (
        <strong className="ces-summary-strong">{describeRecency(value)}</strong>
        )
      </>
    ) : null}
  </>
);

const getMonthsSinceDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  let years = now.getFullYear() - date.getFullYear();
  let months = now.getMonth() - date.getMonth();
  let days = now.getDate() - date.getDate();

  let totalMonths = years * 12 + months;
  if (days < 0) totalMonths -= 1;
  return totalMonths > 0 ? String(totalMonths) : "0";
};

const computeDateFromMonths = (months) => {
  const parsed = Number(months);
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  const result = new Date();
  result.setMonth(result.getMonth() - parsed);
  return result.toISOString().split("T")[0];
};

const mapServerConfigToForm = (serverConfig) => {
  const safeNumber = (value) =>
    value != null && value !== "" ? String(value) : "";

  const advancedData = serverConfig?.advanced || {};
  const legalData = serverConfig?.legal || {};
  const recencyData = serverConfig?.recency || {};

  return {
    autoReject: {
      protesto: !!serverConfig?.autoRejectProtesto,
      protestoMinValue: safeCurrency(serverConfig?.protestoMinValue),
      chequeSemFundo: !!serverConfig?.autoRejectChequeSemFundo,
      chequeMinCount: safeNumber(serverConfig?.chequeMinCount),
      pendenciaFinanceira: !!serverConfig?.autoRejectPendenciaFinanceira,
      pendenciaMinValue: safeCurrency(serverConfig?.pendenciaMinValue),
    },
    scoreThreshold: safeNumber(serverConfig?.scoreThreshold),
    foundationMinAgeMonths: getMonthsSinceDate(serverConfig?.foundationMinDate),
    notes: serverConfig?.notes ?? "",
    advanced: {
      behavioral: {
        active: !!advancedData.behavioral?.active,
        minScore: safeNumber(advancedData.behavioral?.minScore),
      },
      location: {
        active: !!advancedData.location?.active,
        mode: advancedData.location?.mode || "blocked",
        states: advancedData.location?.states || "",
      },
      companySize: {
        active: !!advancedData.companySize?.active,
        porte: advancedData.companySize?.porte || "",
      },
      creditLimit: {
        active: !!advancedData.creditLimit?.active,
        minValue: safeCurrency(advancedData.creditLimit?.minValue),
      },
      faturamento: {
        active: !!advancedData.faturamento?.active,
        minValue: safeCurrency(advancedData.faturamento?.minValue),
      },
      age: {
        active: !!advancedData.age?.active,
        minAge: safeNumber(advancedData.age?.minAge),
        maxAge: safeNumber(advancedData.age?.maxAge),
      },
    },
    legal: {
      acaoCivil: !!legalData.acaoCivil,
      recuperacaoJudicial: !!legalData.recuperacaoJudicial,
      processosLesivos: !!legalData.processosLesivos,
      processosImageUrl: legalData.processosImageUrl || "",
    },
    recency: {
      protesto: recencyData.protesto || "",
      chequeSemFundo: recencyData.chequeSemFundo || "",
      pendenciaFinanceira: recencyData.pendenciaFinanceira || "",
      acaoCivil: recencyData.acaoCivil || "",
      recuperacaoJudicial: recencyData.recuperacaoJudicial || "",
      processosLesivos: recencyData.processosLesivos || "",
    },
  };
};

const LOCATION_MODE_LABELS = {
  blocked: "Bloquear",
  allowed: "Permitir",
};

const BRAZIL_UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const parseUfList = (value) => {
  if (!value) return [];
  return Array.from(
    new Set(
      value
        .split(/[,;\s]+/)
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean)
    )
  );
};

const COMPANY_SIZE_LABELS = {
  micro: "Microempresa",
  pequena: "Empresa de Pequeno Porte",
  media: "Empresa de Médio Porte",
  grande: "Empresa de Grande Porte",
};

const SkeletonBlock = ({ className }) => (
  <div className={cx("ces-skeleton", className)} />
);

export default function CreditEngineSettingsPage() {
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("current");
  const [serverConfig, setServerConfig] = useState(null);
  const [selectedQueryId, setSelectedQueryId] = useState("");
  const [updatingPreset, setUpdatingPreset] = useState(false);
  const [alert, setAlert] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [editingPresetId, setEditingPresetId] = useState(null);

  const { userPermission } = useApplication();
  const canEdit = useMemo(
    () => ["ADMIN", "POWER_USER"].includes(userPermission),
    [userPermission]
  );

  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading: permissionsLoading, data: userPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axiosInstance.get(
        USERS.GET_USER_QUERY_PERMISSIONS
      );
      return items.data;
    }
  );

  const hasCreditEnginePermission = useMemo(() => {
    if (!Array.isArray(userPermissions)) return false;
    return userPermissions.some((item) => {
      const url = item?.url || "";
      if (typeof url !== "string") return false;
      return (
        url.includes("motor-credito-pf") || url.includes("motor-credito-pj")
      );
    });
  }, [userPermissions]);

  const availableQueries = useMemo(() => {
    return (userPermissions || [])
      .filter((q) => {
        const isAdditional =
          typeof q.isAdditional === "boolean"
            ? q.isAdditional
            : Number(q.isAdditional) === 1;
        return q.status != -1 && !isAdditional;
      })
      .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
  }, [userPermissions]);

  const selectedQuery = useMemo(() => {
    if (!selectedQueryId) return null;
    return availableQueries.find(
      (q) => String(q.id) === String(selectedQueryId)
    );
  }, [availableQueries, selectedQueryId]);

  const selectedPreset = useMemo(() => {
    if (!selectedPresetId || selectedPresetId === "current") return null;
    return presets.find((preset) => String(preset.id) === selectedPresetId);
  }, [presets, selectedPresetId]);

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      setLoadingConfig(false);
      setServerConfig(null);
      setFormState(DEFAULT_FORM_STATE);
      setSelectedQueryId("");
      setTemplateName("");
      setSelectedPresetId("current");
      setPresets([]);
      setPresetsLoading(false);

      const presetFromNav = location?.state?.preset;
      if (presetFromNav?.config) {
        setFormState(mapServerConfigToForm(presetFromNav.config));
        const presetQueryId =
          presetFromNav?.queryId ?? presetFromNav?.config?.queryId;
        if (normalizeQueryId(presetQueryId) != null) {
          setSelectedQueryId(String(presetQueryId));
        }
        if (presetFromNav?.name) {
          setTemplateName(presetFromNav.name);
        }
        if (presetFromNav?.id) {
          setEditingPresetId(String(presetFromNav.id));
        }
        setWizardStep(2);
      }
    }

    return () => {
      mounted = false;
    };
  }, []);

  // permissao de consulta nao bloqueia o acesso a esta tela

  const handleToggleAutoReject = (field) => (event) => {
    const checked = event.target.checked;
    setFormState((prev) => ({
      ...prev,
      autoReject: {
        ...prev.autoReject,
        [field]: checked,
      },
    }));
  };

  const handleScoreChange = (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      setFormState((prev) => ({ ...prev, scoreThreshold: value }));
    }
  };

  const handleAutoRejectCurrencyChange = (field) => (event) => {
    const { value } = event.target;
    const formatted = value === "" ? "" : formatCurrencyInput(value);
    setFormState((prev) => ({
      ...prev,
      autoReject: {
        ...prev.autoReject,
        [field]: formatted,
      },
    }));
  };

  const handleNumberField = (field) => (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      setFormState((prev) => ({
        ...prev,
        autoReject: {
          ...prev.autoReject,
          [field]: value,
        },
      }));
    }
  };

  const handleRecencyChange = (field) => (event) => {
    setFormState((prev) => ({
      ...prev,
      recency: {
        ...prev.recency,
        [field]: event.target.value,
      },
    }));
  };

  const handleFoundationAgeChange = (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      setFormState((prev) => ({
        ...prev,
        foundationMinAgeMonths: value,
      }));
    }
  };

  const handleNotesChange = (event) => {
    setFormState((prev) => ({ ...prev, notes: event.target.value }));
  };

  const handleAdvancedToggle = (section) => (event) => {
    const checked = event.target.checked;
    setFormState((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        [section]: {
          ...prev.advanced[section],
          active: checked,
        },
      },
    }));
  };

  const handleAdvancedChange = (section, field) => (value) => {
    setFormState((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        [section]: {
          ...prev.advanced[section],
          [field]: value,
        },
      },
    }));
  };

  const handleToggleUf = (uf) => {
    setFormState((prev) => {
      const current = parseUfList(prev.advanced.location.states);
      const exists = current.includes(uf);
      const next = exists
        ? current.filter((item) => item !== uf)
        : [...current, uf];
      return {
        ...prev,
        advanced: {
          ...prev.advanced,
          location: {
            ...prev.advanced.location,
            states: next.join(", "),
          },
        },
      };
    });
  };

  const handleAdvancedCurrencyChange = (section, field) => (event) => {
    const { value } = event.target;
    const formatted = value === "" ? "" : formatCurrencyInput(value);
    handleAdvancedChange(section, field)(formatted);
  };

  const handleLegalToggle = (field) => (event) => {
    const checked = event.target.checked;
    setFormState((prev) => ({
      ...prev,
      legal: {
        ...prev.legal,
        [field]: checked,
      },
    }));
  };

  const handleLegalLinkChange = (event) => {
    setFormState((prev) => ({
      ...prev,
      legal: {
        ...prev.legal,
        processosImageUrl: event.target.value,
      },
    }));
  };

  const handleAdvancedNumericChange = (section, field) => (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      handleAdvancedChange(section, field)(value);
    }
  };

  const buildConfigPayload = () => ({
    autoRejectProtesto: !!formState.autoReject.protesto,
    autoRejectChequeSemFundo: !!formState.autoReject.chequeSemFundo,
    autoRejectPendenciaFinanceira: !!formState.autoReject.pendenciaFinanceira,
    protestoMinValue: toNumber(formState.autoReject.protestoMinValue),
    chequeMinCount:
      formState.autoReject.chequeMinCount === ""
        ? null
        : Number(formState.autoReject.chequeMinCount),
    pendenciaMinValue: toNumber(formState.autoReject.pendenciaMinValue),
    scoreThreshold:
      formState.scoreThreshold === "" ? null : Number(formState.scoreThreshold),
    foundationMinDate: computeDateFromMonths(formState.foundationMinAgeMonths),
    notes: formState.notes || null,
    advanced: {
      behavioral: {
        active: !!formState.advanced.behavioral.active,
        minScore:
          formState.advanced.behavioral.minScore === ""
            ? null
            : Number(formState.advanced.behavioral.minScore),
      },
      location: {
        active: !!formState.advanced.location.active,
        mode: formState.advanced.location.mode,
        states:
          formState.advanced.location.states.trim() === ""
            ? null
            : formState.advanced.location.states.trim(),
      },
      companySize: {
        active: !!formState.advanced.companySize.active,
        porte:
          formState.advanced.companySize.porte.trim() === ""
            ? null
            : formState.advanced.companySize.porte.trim(),
      },
      creditLimit: {
        active: !!formState.advanced.creditLimit.active,
        minValue: toNumber(formState.advanced.creditLimit.minValue),
      },
      faturamento: {
        active: !!formState.advanced.faturamento.active,
        minValue: toNumber(formState.advanced.faturamento.minValue),
      },
      age: {
        active: !!formState.advanced.age.active,
        minAge:
          formState.advanced.age.minAge === ""
            ? null
            : Number(formState.advanced.age.minAge),
        maxAge:
          formState.advanced.age.maxAge === ""
            ? null
            : Number(formState.advanced.age.maxAge),
      },
    },
    legal: {
      acaoCivil: !!formState.legal.acaoCivil,
      recuperacaoJudicial: !!formState.legal.recuperacaoJudicial,
      processosLesivos: !!formState.legal.processosLesivos,
      processosImageUrl:
        formState.legal.processosImageUrl.trim() === ""
          ? null
          : formState.legal.processosImageUrl.trim(),
    },
    recency: {
      protesto: formState.recency.protesto || null,
      chequeSemFundo: formState.recency.chequeSemFundo || null,
      pendenciaFinanceira: formState.recency.pendenciaFinanceira || null,
      acaoCivil: formState.recency.acaoCivil || null,
      recuperacaoJudicial: formState.recency.recuperacaoJudicial || null,
      processosLesivos: formState.recency.processosLesivos || null,
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit) {
      setAlert({
        type: "warning",
        message: "Você não tem permissão para editar o motor de crédito.",
      });
      return;
    }
    if (!selectedQueryId) {
      setAlert({
        type: "warning",
        message: "Selecione uma consulta base antes de salvar.",
      });
      setWizardStep(1);
      return;
    }
    if (!templateName.trim()) {
      setAlert({
        type: "warning",
        message: "Informe um nome para o template antes de salvar.",
      });
      return;
    }

    setSavingConfig(true);
    setAlert(null);
    try {
      const payload = buildConfigPayload();
      payload.queryId = normalizeQueryId(selectedQueryId);
      if (editingPresetId) {
        await axiosInstance.put(
          ADMIN.UPDATE_CREDIT_ENGINE_PRESET(editingPresetId),
          {
            name: templateName.trim(),
            queryId: payload.queryId,
            config: payload,
          }
        );
      } else {
        await axiosInstance.post(ADMIN.CREATE_CREDIT_ENGINE_PRESET, {
          name: templateName.trim(),
          queryId: payload.queryId,
          config: payload,
        });
      }

      const { data } = await axiosInstance.post(
        ADMIN.SAVE_ENGINE_CREDIT,
        payload
      );
      const responseConfig = data?.data || data || payload;
      setFormState(mapServerConfigToForm(responseConfig));
      setAlert({
        type: "success",
        message: editingPresetId
          ? "Template atualizado com sucesso."
          : "Configurações e template salvos com sucesso.",
      });
      window.dispatchEvent(new Event("credit-engine-config-updated"));
      navigate("/credit-engine");
    } catch (error) {
      console.error(error);
      setAlert({
        type: "error",
        message: "Erro ao salvar as configurações. Tente novamente.",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!canEdit) {
      setAlert({
        type: "warning",
        message: "Você não tem permissão para salvar templates.",
      });
      return;
    }
    if (!templateName.trim()) {
      setAlert({
        type: "warning",
        message: "Informe um nome para o template.",
      });
      return;
    }

    setSavingTemplate(true);
    setAlert(null);
    try {
      const configPayload = buildConfigPayload();
      configPayload.queryId = normalizeQueryId(selectedQueryId);
      await axiosInstance.post(ADMIN.CREATE_CREDIT_ENGINE_PRESET, {
        name: templateName.trim(),
        queryId: configPayload.queryId,
        config: configPayload,
      });
      setTemplateName("");
      setAlert({
        type: "success",
        message: "Template do motor salvo com sucesso.",
      });
    } catch (error) {
      console.error(error);
      setAlert({
        type: "error",
        message: "Erro ao salvar o template. Tente novamente.",
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePresetSelect = (event) => {
    const value = event.target.value;
    setSelectedPresetId(value);

    if (value === "current") {
      if (serverConfig) {
        setFormState(mapServerConfigToForm(serverConfig));
      }
      setSelectedQueryId("");
      return;
    }

    const preset = presets.find((item) => String(item.id) === value);
    if (preset?.config) {
      setFormState(mapServerConfigToForm(preset.config));
      setSelectedQueryId(
        normalizeQueryId(preset.queryId) != null
          ? String(preset.queryId)
          : normalizeQueryId(preset.config?.queryId) != null
          ? String(preset.config.queryId)
          : ""
      );
      setAlert({
        type: "info",
        message: `Template "${preset.name}" carregado. Revise e salve para aplicar.`,
      });
    }
  };

  const handleUpdateTemplate = async () => {
    if (!canEdit) {
      setAlert({
        type: "warning",
        message: "Você não tem permissão para atualizar templates.",
      });
      return;
    }
    if (!selectedPreset) {
      setAlert({
        type: "warning",
        message: "Selecione um template para atualizar.",
      });
      return;
    }

    setUpdatingPreset(true);
    setAlert(null);
    try {
      const configPayload = buildConfigPayload();
      const queryId = normalizeQueryId(selectedQueryId);
      await axiosInstance.put(
        ADMIN.UPDATE_CREDIT_ENGINE_PRESET(selectedPreset.id),
        {
          name: selectedPreset.name,
          queryId,
          config: { ...configPayload, queryId },
        }
      );
      setPresets((prev) =>
        prev.map((preset) =>
          preset.id === selectedPreset.id
            ? {
                ...preset,
                queryId,
                config: { ...configPayload, queryId },
              }
            : preset
        )
      );
      setAlert({
        type: "success",
        message: `Template "${selectedPreset.name}" atualizado com sucesso.`,
      });
    } catch (error) {
      console.error(error);
      setAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          "Erro ao atualizar o template. Tente novamente.",
      });
    } finally {
      setUpdatingPreset(false);
    }
  };

  const locationModeLabel =
    LOCATION_MODE_LABELS[formState.advanced.location.mode] ||
    (formState.advanced.location.mode === "allowed" ? "Permitir" : "Bloquear");
  const locationStatesText = formState.advanced.location.states.trim();
  const foundationAgeMonths = formState.foundationMinAgeMonths.trim();
  const companySizeLabel =
    COMPANY_SIZE_LABELS[formState.advanced.companySize.porte] ||
    formState.advanced.companySize.porte;
  const lastUpdatedLabel = formatDateForDisplay(serverConfig?.updatedAt);
  const scoreValue = (() => {
    const parsed = Number(formState.scoreThreshold);
    return Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 1000) : 0;
  })();
  const scoreTone =
    scoreValue < 450 ? "low" : scoreValue < 700 ? "mid" : "high";
  const selectedUfs = parseUfList(formState.advanced.location.states);
  const ageMinValue = (() => {
    const parsed = Number(formState.advanced.age.minAge);
    return Number.isFinite(parsed)
      ? Math.min(Math.max(parsed, 0), AGE_RANGE_MAX)
      : 0;
  })();
  const ageMaxValue = (() => {
    if (formState.advanced.age.maxAge === "") return AGE_RANGE_MAX;
    const parsed = Number(formState.advanced.age.maxAge);
    return Number.isFinite(parsed)
      ? Math.min(Math.max(parsed, 0), AGE_RANGE_MAX)
      : AGE_RANGE_MAX;
  })();
  const ageMinLabel =
    formState.advanced.age.minAge === ""
      ? "Sem mínimo"
      : `${formState.advanced.age.minAge} anos`;
  const ageMaxLabel =
    formState.advanced.age.maxAge === ""
      ? "Sem máximo"
      : `${formState.advanced.age.maxAge} anos`;

  const summaryItems = [
    {
      id: "protesto",
      active: formState.autoReject.protesto,
      text: appendRecencyNode(
        formState.autoReject.protestoMinValue ? (
          <>
            Reprovar se protestos atingirem{" "}
            {boldValue(formatCurrency(formState.autoReject.protestoMinValue))}.
          </>
        ) : (
          "Reprovar quando houver qualquer protesto."
        ),
        formState.recency.protesto
      ),
    },
    {
      id: "cheque",
      active: formState.autoReject.chequeSemFundo,
      text: appendRecencyNode(
        formState.autoReject.chequeMinCount ? (
          <>
            Reprovar a partir de{" "}
            {boldValue(formState.autoReject.chequeMinCount)} cheque(s).
          </>
        ) : (
          "Reprovar com qualquer quantidade de cheques sem fundo."
        ),
        formState.recency.chequeSemFundo
      ),
    },
    {
      id: "pendencia",
      active: formState.autoReject.pendenciaFinanceira,
      text: appendRecencyNode(
        formState.autoReject.pendenciaMinValue ? (
          <>
            Reprovar se pendências atingirem{" "}
            {boldValue(formatCurrency(formState.autoReject.pendenciaMinValue))}.
          </>
        ) : (
          "Reprovar com qualquer pendência financeira."
        ),
        formState.recency.pendenciaFinanceira
      ),
    },
    {
      id: "score",
      active: !!formState.scoreThreshold,
      text: <>Score mínimo {boldValue(formState.scoreThreshold)}.</>,
    },
    {
      id: "foundation",
      active: Boolean(foundationAgeMonths && Number(foundationAgeMonths) > 0),
      text: (
        <>
          Reprovar empresas com menos de {boldValue(foundationAgeMonths)} meses
          de fundação.
        </>
      ),
    },
    {
      id: "behavioral",
      active:
        formState.advanced.behavioral.active &&
        !!formState.advanced.behavioral.minScore,
      text: (
        <>
          Reprovar com comportamento abaixo de{" "}
          {boldValue(formState.advanced.behavioral.minScore)}.
        </>
      ),
    },
    {
      id: "location",
      active:
        formState.advanced.location.active && locationStatesText.length > 0,
      text: (
        <>
          {boldValue(locationModeLabel)} os estados{" "}
          {boldValue(locationStatesText)}.
        </>
      ),
    },
    {
      id: "companySize",
      active:
        formState.advanced.companySize.active && companySizeLabel.length > 0,
      text: <>Porte mínimo: {boldValue(companySizeLabel)}.</>,
    },
    {
      id: "creditLimit",
      active:
        formState.advanced.creditLimit.active &&
        !!formState.advanced.creditLimit.minValue,
      text: (
        <>
          Limite mínimo{" "}
          {boldValue(formatCurrency(formState.advanced.creditLimit.minValue))}.
        </>
      ),
    },
    {
      id: "faturamento",
      active:
        formState.advanced.faturamento.active &&
        !!formState.advanced.faturamento.minValue,
      text: (
        <>
          Faturamento mínimo{" "}
          {boldValue(formatCurrency(formState.advanced.faturamento.minValue))}.
        </>
      ),
    },
    {
      id: "age",
      active:
        formState.advanced.age.active &&
        (formState.advanced.age.minAge || formState.advanced.age.maxAge),
      text: (
        <>
          Faixa de idade {boldValue(formState.advanced.age.minAge || "0")} até{" "}
          {boldValue(formState.advanced.age.maxAge || "∞")}.
        </>
      ),
    },
    {
      id: "acaoCivil",
      active: formState.legal.acaoCivil,
      text: appendRecencyNode(
        "Reprovar por ocorrência de ação cível.",
        formState.recency.acaoCivil
      ),
    },
    {
      id: "recuperacaoJudicial",
      active: formState.legal.recuperacaoJudicial,
      text: appendRecencyNode(
        "Reprovar empresas em recuperação judicial.",
        formState.recency.recuperacaoJudicial
      ),
    },
    {
      id: "processosLesivos",
      active: formState.legal.processosLesivos,
      text: appendRecencyNode(
        formState.legal.processosImageUrl ? (
          <>
            Referência visual: {boldValue(formState.legal.processosImageUrl)}.
          </>
        ) : (
          "Reprovar quando houver processos lesivos."
        ),
        formState.recency.processosLesivos
      ),
    },
  ];
  const visibleSummaryItems = summaryItems.filter((item) => item.active);
  const activeRulesCount = visibleSummaryItems.length;
  const templateLabel =
    selectedPresetId === "current"
      ? "Configuração atual"
      : selectedPreset?.name || "Template selecionado";
  const queryLabel = selectedQuery?.name || "Consulta base não definida";
  const canAdvanceWizard = Boolean(selectedQueryId);

  const wizardSteps = [
    {
      id: 1,
      title: "Consulta base",
      description: "Defina a fonte principal para o motor.",
    },
    {
      id: 2,
      title: "Reprovação automática",
      description: "Protestos, cheques, pendências, score e fundação.",
    },
    {
      id: 3,
      title: "Filtros avançados",
      description: "Comportamento, localização, porte e limites.",
    },
    {
      id: 4,
      title: "Monitoramento legal",
      description: "Ações, recuperação judicial e processos.",
    },
    {
      id: 5,
      title: "Resumo final",
      description: "Resumo, observações adicionais e template.",
    },
  ];
  const isRulesStep = wizardStep > 1;
  const isLastStep = wizardStep === wizardSteps.length;
  const currentStep = wizardSteps.find((step) => step.id === wizardStep);

  return (
    <div>
      <div className="credit-engine-settings-page">
        <div className="ces-shell">
          <header className="ces-page-header">
            <div className="ces-header-main">
              <div className="ces-title-block">
                <span className="ces-eyebrow">Motor de crédito</span>
                <h1 className="ces-title">Configuração corporativa</h1>
                <p className="ces-subtitle">
                  Defina regras de risco, compliance e limites financeiros com
                  visibilidade total antes de publicar o motor.
                </p>
              </div>
              <div className="ces-header-actions">
                <Button
                  className="ces-back-button"
                  onClick={() => navigate("/credit-engine")}
                  size={"sm"}
                >
                  <div className="flex items-center gap-2">
                    <HiArrowLeft className="w-4 h-4" />
                    <span>Voltar ao motor</span>
                  </div>
                </Button>
              </div>
            </div>
          </header>

          {alert && (
            <Alert
              className="ces-alert"
              color={
                alert.type === "error"
                  ? "failure"
                  : alert.type === "success"
                  ? "success"
                  : alert.type === "warning"
                  ? "warning"
                  : "info"
              }
              icon={
                alert.type === "success"
                  ? HiOutlineShieldCheck
                  : HiOutlineExclamationCircle
              }
              onDismiss={() => setAlert(null)}
            >
              {alert.message}
            </Alert>
          )}

          {!canEdit && (
            <Alert color="warning" icon={HiOutlineExclamationCircle}>
              <span className="font-medium">Permissão limitada.</span> Apenas
              usuários ADMIN ou POWER_USER podem alterar as regras.
            </Alert>
          )}

          {loadingConfig && (
            <div className="ces-skeleton-wrapper">
              <div className="ces-skeleton-tabs">
                <SkeletonBlock className="ces-skeleton-pill" />
                <SkeletonBlock className="ces-skeleton-pill" />
                <SkeletonBlock className="ces-skeleton-pill" />
              </div>

              <div className="ces-skeleton-grid">
                <div className="ces-skeleton-left">
                  <div className="ces-skeleton-card">
                    <div className="ces-skeleton-header">
                      <SkeletonBlock className="ces-skeleton-icon" />
                      <div className="ces-skeleton-lines">
                        <SkeletonBlock className="ces-skeleton-line lg" />
                        <SkeletonBlock className="ces-skeleton-line md" />
                      </div>
                    </div>
                    <div className="ces-skeleton-body">
                      <SkeletonBlock className="ces-skeleton-row" />
                      <SkeletonBlock className="ces-skeleton-row" />
                      <SkeletonBlock className="ces-skeleton-row" />
                      <SkeletonBlock className="ces-skeleton-row" />
                    </div>
                  </div>

                  <div className="ces-skeleton-card">
                    <div className="ces-skeleton-header">
                      <SkeletonBlock className="ces-skeleton-icon" />
                      <div className="ces-skeleton-lines">
                        <SkeletonBlock className="ces-skeleton-line lg" />
                        <SkeletonBlock className="ces-skeleton-line md" />
                      </div>
                    </div>
                    <div className="ces-skeleton-body">
                      <SkeletonBlock className="ces-skeleton-row" />
                      <SkeletonBlock className="ces-skeleton-row" />
                      <SkeletonBlock className="ces-skeleton-row" />
                    </div>
                  </div>
                </div>

                <div className="ces-skeleton-right">
                  <div className="ces-skeleton-card ces-skeleton-side">
                    <div className="ces-skeleton-header">
                      <div className="ces-skeleton-lines">
                        <SkeletonBlock className="ces-skeleton-line lg" />
                        <SkeletonBlock className="ces-skeleton-line sm" />
                      </div>
                    </div>
                    <div className="ces-skeleton-body">
                      <SkeletonBlock className="ces-skeleton-line md" />
                      <SkeletonBlock className="ces-skeleton-line md" />
                      <SkeletonBlock className="ces-skeleton-line md" />
                      <SkeletonBlock className="ces-skeleton-line md" />
                      <SkeletonBlock className="ces-skeleton-line sm" />
                      <SkeletonBlock className="ces-skeleton-textarea" />
                      <div className="ces-skeleton-template-row">
                        <SkeletonBlock className="ces-skeleton-input" />
                        <SkeletonBlock className="ces-skeleton-button" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingConfig && (
            <form className="ces-grid" onSubmit={handleSubmit}>
              <div className="ces-left-column">
                <div className="ces-wizard">
                  {wizardSteps.map((step) => (
                    <div
                      key={step.id}
                      className={cx(
                        "ces-wizard-step",
                        wizardStep === step.id && "ces-wizard-step--active",
                        wizardStep > step.id && "ces-wizard-step--done"
                      )}
                    >
                      <div className="ces-wizard-indicator">
                        {wizardStep === step.id ? (
                          <Spinner size="sm" />
                        ) : (
                          step.id
                        )}
                      </div>
                      <div>
                        <span className="ces-wizard-title">{step.title}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {wizardStep === 1 && (
                  <section className="ces-card ces-tab-panel" role="tabpanel">
                    <div className="ces-card-header">
                      <div className="ces-card-icon">
                        <HiOutlineShieldCheck className="ces-card-icon-inner" />
                      </div>
                      <div>
                        <h2>Defina a consulta base</h2>
                        <p className="text-sm text-slate-500">
                          Selecione a consulta principal para alimentar as
                          regras do motor de crédito.
                        </p>
                      </div>
                    </div>
                    <div className="ces-card-body space-y-4">
                      <div>
                        <Label
                          htmlFor="wizardQuerySelect"
                          value="Consulta base"
                          className="text-xs font-medium text-slate-700"
                        />
                        <Select
                          id="wizardQuerySelect"
                          value={selectedQueryId}
                          onChange={(event) =>
                            setSelectedQueryId(event.target.value)
                          }
                          disabled={availableQueries.length === 0}
                          className="mt-2"
                        >
                          <option value="">Selecione uma consulta</option>
                          {availableQueries.map((query) => (
                            <option key={query.id} value={String(query.id)}>
                              {query.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="ces-step-actions">
                        <Button
                          type="button"
                          onClick={() => setWizardStep(2)}
                          disabled={!canAdvanceWizard}
                        >
                          Continuar
                        </Button>
                      </div>
                    </div>
                  </section>
                )}

                {wizardStep === 2 && (
                  <section className="ces-card ces-tab-panel" role="tabpanel">
                    <div className="ces-card-header">
                      <div className="ces-card-icon">
                        <HiSparkles className="ces-card-icon-inner" />
                      </div>
                      <div>
                        <h2>Regras de reprovação automática</h2>
                        <p className="text-sm text-slate-500">
                          Controle protestos, cheques, pendências, score e
                          fundação em uma única camada de reprovação automática.
                        </p>
                      </div>
                    </div>
                    <div className="ces-card-body space-y-1">
                      <RuleCheckbox
                        label="Reprovar por protestos"
                        description="A reprovação dispara quando o total de protestos ultrapassa um valor."
                        checked={formState.autoReject.protesto}
                        onChange={handleToggleAutoReject("protesto")}
                        disabled={!canEdit}
                      >
                        <div className="ces-rule-grid">
                          <div className="ces-field">
                            <Label
                              htmlFor="protestoMinValue"
                              value="Valor mínimo de protestos"
                              className="text-xs font-medium text-slate-700"
                            />
                            <TextInput
                              id="protestoMinValue"
                              type="text"
                              placeholder="Ex: 1.000,00"
                              value={formState.autoReject.protestoMinValue}
                              onChange={handleAutoRejectCurrencyChange(
                                "protestoMinValue"
                              )}
                              disabled={
                                !canEdit || !formState.autoReject.protesto
                              }
                            />
                          </div>
                          <div className="ces-field">
                            <Label
                              value="Recência da última restrição"
                              className="text-xs font-medium text-slate-700"
                            />
                            <Select
                              value={formState.recency.protesto}
                              onChange={handleRecencyChange("protesto")}
                              disabled={
                                !canEdit || !formState.autoReject.protesto
                              }
                            >
                              {RECENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Reprovar por cheques sem fundo"
                        description="Mostra a quantidade mínima de cheques que dispara a reprovação."
                        checked={formState.autoReject.chequeSemFundo}
                        onChange={handleToggleAutoReject("chequeSemFundo")}
                        disabled={!canEdit}
                      >
                        <div className="ces-rule-grid">
                          <div className="ces-field">
                            <Label
                              htmlFor="chequeMinCount"
                              value="Quantidade mínima de cheques"
                              className="text-xs font-medium text-slate-700"
                            />
                            <TextInput
                              id="chequeMinCount"
                              type="text"
                              placeholder="Ex: 1, 2, 3..."
                              value={formState.autoReject.chequeMinCount}
                              onChange={handleNumberField("chequeMinCount")}
                              disabled={
                                !canEdit || !formState.autoReject.chequeSemFundo
                              }
                            />
                          </div>
                          <div className="ces-field">
                            <Label
                              value="Recência da última restrição"
                              className="text-xs font-medium text-slate-700"
                            />
                            <Select
                              value={formState.recency.chequeSemFundo}
                              onChange={handleRecencyChange("chequeSemFundo")}
                              disabled={
                                !canEdit || !formState.autoReject.chequeSemFundo
                              }
                            >
                              {RECENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Reprovar por pendências financeiras"
                        description="Abra a opção quando o valor total de pendências for perigoso."
                        checked={formState.autoReject.pendenciaFinanceira}
                        onChange={handleToggleAutoReject("pendenciaFinanceira")}
                        disabled={!canEdit}
                      >
                        <div className="ces-rule-grid">
                          <div className="ces-field">
                            <Label
                              htmlFor="pendenciaMinValue"
                              value="Valor mínimo de pendências"
                              className="text-xs font-medium text-slate-700"
                            />
                            <TextInput
                              id="pendenciaMinValue"
                              type="text"
                              placeholder="Ex: 5.000,00"
                              value={formState.autoReject.pendenciaMinValue}
                              onChange={handleAutoRejectCurrencyChange(
                                "pendenciaMinValue"
                              )}
                              disabled={
                                !canEdit ||
                                !formState.autoReject.pendenciaFinanceira
                              }
                            />
                          </div>
                          <div className="ces-field">
                            <Label
                              value="Recência da última restrição"
                              className="text-xs font-medium text-slate-700"
                            />
                            <Select
                              value={formState.recency.pendenciaFinanceira}
                              onChange={handleRecencyChange(
                                "pendenciaFinanceira"
                              )}
                              disabled={
                                !canEdit ||
                                !formState.autoReject.pendenciaFinanceira
                              }
                            >
                              {RECENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </RuleCheckbox>
                    </div>
                  </section>
                )}

                {wizardStep === 3 && (
                  <section className="ces-card ces-tab-panel" role="tabpanel">
                    <div className="ces-card-header">
                      <div className="ces-card-icon">
                        <HiOutlineShieldCheck className="ces-card-icon-inner" />
                      </div>
                      <div>
                        <h2>Filtros avançados</h2>
                        <p className="text-sm text-slate-500">
                          Use regras complementares para comportamento,
                          localização, porte, limites e idade.
                        </p>
                      </div>
                    </div>

                    <div className="ces-card-body space-y-1">
                      <div className="flex flex-col gap-4 md:flex-row">
                        <div className="flex-1">
                          <Label
                            htmlFor="scoreThreshold"
                            value="Score mínimo"
                            className="text-xs font-medium text-slate-700"
                          />
                          <div className="ces-score-control">
                            <div className="ces-score-header">
                              <span className="ces-score-value">
                                {scoreValue}
                              </span>
                              <span
                                className={cx(
                                  "ces-score-pill",
                                  scoreTone === "low" && "ces-score-pill--low",
                                  scoreTone === "mid" && "ces-score-pill--mid",
                                  scoreTone === "high" && "ces-score-pill--high"
                                )}
                              >
                                {scoreTone === "low"
                                  ? "Risco alto"
                                  : scoreTone === "mid"
                                  ? "Risco moderado"
                                  : "Risco baixo"}
                              </span>
                            </div>
                            <input
                              id="scoreThreshold"
                              type="range"
                              min="0"
                              max="1000"
                              step="10"
                              value={scoreValue}
                              onChange={handleScoreChange}
                              disabled={!canEdit}
                              className={cx(
                                "ces-score-range",
                                scoreTone === "low" && "ces-score-range--low",
                                scoreTone === "mid" && "ces-score-range--mid",
                                scoreTone === "high" && "ces-score-range--high"
                              )}
                              style={{
                                "--score-fill": `${(scoreValue / 1000) * 100}%`,
                              }}
                            />
                            <div className="ces-score-scale">
                              <span>0</span>
                              <span>500</span>
                              <span>1000</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <RuleCheckbox
                        label="Localização"
                        description="Bloquear ou permitir apenas filtros por estados."
                        checked={formState.advanced.location.active}
                        onChange={handleAdvancedToggle("location")}
                        disabled={!canEdit}
                      >
                        <div className="ces-rule-grid">
                          <div className="ces-field">
                            <Label
                              htmlFor="locationMode"
                              value="Modo"
                              className="text-xs font-medium text-slate-700"
                            />
                            <Select
                              id="locationMode"
                              value={formState.advanced.location.mode}
                              onChange={(event) =>
                                handleAdvancedChange(
                                  "location",
                                  "mode"
                                )(event.target.value)
                              }
                              disabled={
                                !canEdit || !formState.advanced.location.active
                              }
                            >
                              <option value="blocked">
                                Estados bloqueados
                              </option>
                              <option value="allowed">
                                Apenas estados permitidos
                              </option>
                            </Select>
                          </div>
                          <div className="ces-field">
                            <Label
                              value="UFs"
                              className="text-xs font-medium text-slate-700"
                            />
                            <div className="ces-uf-grid">
                              {BRAZIL_UFS.map((uf) => {
                                const active = selectedUfs.includes(uf);
                                return (
                                  <button
                                    key={uf}
                                    type="button"
                                    className={cx(
                                      "ces-uf-pill",
                                      active && "ces-uf-pill--active"
                                    )}
                                    onClick={() => handleToggleUf(uf)}
                                    disabled={
                                      !canEdit ||
                                      !formState.advanced.location.active
                                    }
                                  >
                                    {uf}
                                  </button>
                                );
                              })}
                            </div>
                            <span className="ces-template-helper">
                              Selecionadas:{" "}
                              {selectedUfs.length > 0
                                ? selectedUfs.join(", ")
                                : "Nenhuma"}
                            </span>
                          </div>
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Porte da empresa"
                        description="Exija um porte mínimo para aprovar automaticamente."
                        checked={formState.advanced.companySize.active}
                        onChange={handleAdvancedToggle("companySize")}
                        disabled={!canEdit}
                      >
                        <div className="ces-field">
                          <Label
                            htmlFor="companySize"
                            value="Escolha o porte mínimo"
                            className="text-xs font-medium text-slate-700"
                          />
                          <Select
                            id="companySize"
                            value={formState.advanced.companySize.porte}
                            onChange={(event) =>
                              handleAdvancedChange(
                                "companySize",
                                "porte"
                              )(event.target.value)
                            }
                            disabled={
                              !canEdit || !formState.advanced.companySize.active
                            }
                          >
                            <option value="">Selecione</option>
                            <option value="micro">Microempresa</option>
                            <option value="pequena">
                              Empresa de Pequeno Porte
                            </option>
                            <option value="media">
                              Empresa de Médio Porte
                            </option>
                            <option value="grande">
                              Empresa de Grande Porte
                            </option>
                          </Select>
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Limite de crédito"
                        description="Estabeleça um limite mínimo de crédito para aprovação imediata."
                        checked={formState.advanced.creditLimit.active}
                        onChange={handleAdvancedToggle("creditLimit")}
                        disabled={!canEdit}
                      >
                        <div className="ces-field">
                          <Label
                            htmlFor="creditLimit"
                            value="Limite mínimo (R$)"
                            className="text-xs font-medium text-slate-700"
                          />
                          <TextInput
                            id="creditLimit"
                            type="text"
                            placeholder="Ex: 20.000,00"
                            value={formState.advanced.creditLimit.minValue}
                            onChange={handleAdvancedCurrencyChange(
                              "creditLimit",
                              "minValue"
                            )}
                            disabled={
                              !canEdit || !formState.advanced.creditLimit.active
                            }
                          />
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Faturamento presumido"
                        description="Considere apenas empresas com faturamento presumido acima do mínimo."
                        checked={formState.advanced.faturamento.active}
                        onChange={handleAdvancedToggle("faturamento")}
                        disabled={!canEdit}
                      >
                        <div className="ces-field">
                          <Label
                            htmlFor="faturamento"
                            value="Faturamento mínimo (R$)"
                            className="text-xs font-medium text-slate-700"
                          />
                          <TextInput
                            id="faturamento"
                            type="text"
                            placeholder="Ex: 150.000,00"
                            value={formState.advanced.faturamento.minValue}
                            onChange={handleAdvancedCurrencyChange(
                              "faturamento",
                              "minValue"
                            )}
                            disabled={
                              !canEdit || !formState.advanced.faturamento.active
                            }
                          />
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Restrição de idade"
                        description="Faixa aplicada tanto para pessoas quanto para idade da empresa (em anos)."
                        checked={formState.advanced.age.active}
                        onChange={handleAdvancedToggle("age")}
                        disabled={!canEdit}
                      >
                        <div className="ces-age-control">
                          <div className="ces-age-header">
                            <div>
                              <span className="ces-age-label">
                                Faixa configurada
                              </span>
                              <div className="ces-age-values">
                                <span className="ces-age-pill">
                                  Mínimo: {ageMinLabel}
                                </span>
                                <span className="ces-age-pill">
                                  Máximo: {ageMaxLabel}
                                </span>
                              </div>
                            </div>
                            <span className="ces-age-hint">0-120 anos</span>
                          </div>
                          <div className="ces-age-sliders">
                            <div className="ces-age-slider">
                              <Label
                                htmlFor="minAge"
                                value="Idade mínima"
                                className="text-xs font-medium text-slate-700"
                              />
                              <input
                                id="minAge"
                                type="range"
                                min="0"
                                max={AGE_RANGE_MAX}
                                step="1"
                                value={ageMinValue}
                                onChange={(event) =>
                                  setFormState((prev) => {
                                    const nextAge = {
                                      ...prev.advanced.age,
                                      minAge: event.target.value,
                                    };
                                    if (
                                      prev.advanced.age.maxAge !== "" &&
                                      Number(event.target.value) >
                                        Number(prev.advanced.age.maxAge)
                                    ) {
                                      nextAge.maxAge = event.target.value;
                                    }
                                    return {
                                      ...prev,
                                      advanced: {
                                        ...prev.advanced,
                                        age: nextAge,
                                      },
                                    };
                                  })
                                }
                                disabled={
                                  !canEdit || !formState.advanced.age.active
                                }
                                className="ces-age-range"
                              />
                            </div>
                            <div className="ces-age-slider">
                              <Label
                                htmlFor="maxAge"
                                value="Idade máxima"
                                className="text-xs font-medium text-slate-700"
                              />
                              <input
                                id="maxAge"
                                type="range"
                                min="0"
                                max={AGE_RANGE_MAX}
                                step="1"
                                value={ageMaxValue}
                                onChange={(event) =>
                                  setFormState((prev) => {
                                    const nextAge = {
                                      ...prev.advanced.age,
                                      maxAge: event.target.value,
                                    };
                                    if (
                                      prev.advanced.age.minAge !== "" &&
                                      Number(event.target.value) <
                                        Number(prev.advanced.age.minAge)
                                    ) {
                                      nextAge.minAge = event.target.value;
                                    }
                                    return {
                                      ...prev,
                                      advanced: {
                                        ...prev.advanced,
                                        age: nextAge,
                                      },
                                    };
                                  })
                                }
                                disabled={
                                  !canEdit || !formState.advanced.age.active
                                }
                                className="ces-age-range"
                              />
                            </div>
                          </div>
                        </div>
                      </RuleCheckbox>
                    </div>
                  </section>
                )}

                {wizardStep === 4 && (
                  <section className="ces-card ces-tab-panel" role="tabpanel">
                    <div className="ces-card-header">
                      <div className="ces-card-icon">
                        <HiOutlineShieldCheck className="ces-card-icon-inner" />
                      </div>
                      <div>
                        <h2>Monitoramento legal</h2>
                        <p className="text-sm text-slate-500">
                          Reprovar automaticamente por ocorrências judiciais
                          críticas.
                        </p>
                      </div>
                    </div>
                    <div className="ces-card-body space-y-2">
                      <RuleCheckbox
                        label="Ação cível"
                        description="Dispara quando houver registro de ação cível relevante."
                        checked={formState.legal.acaoCivil}
                        onChange={handleLegalToggle("acaoCivil")}
                        disabled={!canEdit}
                      >
                        <div className="ces-field">
                          <Label
                            value="Recência da restrição"
                            className="text-xs font-medium text-slate-700"
                          />
                          <Select
                            value={formState.recency.acaoCivil}
                            onChange={handleRecencyChange("acaoCivil")}
                            disabled={!canEdit || !formState.legal.acaoCivil}
                          >
                            {RECENCY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Recuperação judicial"
                        description="Reprovar empresas em processo de recuperação judicial."
                        checked={formState.legal.recuperacaoJudicial}
                        onChange={handleLegalToggle("recuperacaoJudicial")}
                        disabled={!canEdit}
                      >
                        <div className="ces-field">
                          <Label
                            value="Recência da restrição"
                            className="text-xs font-medium text-slate-700"
                          />
                          <Select
                            value={formState.recency.recuperacaoJudicial}
                            onChange={handleRecencyChange(
                              "recuperacaoJudicial"
                            )}
                            disabled={
                              !canEdit || !formState.legal.recuperacaoJudicial
                            }
                          >
                            {RECENCY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </RuleCheckbox>

                      <RuleCheckbox
                        label="Ações e processos lesivos"
                        description="Considere processos graves com provas visuais."
                        checked={formState.legal.processosLesivos}
                        onChange={handleLegalToggle("processosLesivos")}
                        disabled={!canEdit}
                      >
                        <div className="ces-rule-grid">
                          <div className="ces-field">
                            <Label
                              htmlFor="processosImageUrl"
                              value="Link da imagem/prova"
                              className="text-xs font-medium text-slate-700"
                            />
                            <TextInput
                              id="processosImageUrl"
                              type="text"
                              placeholder="https://..."
                              value={formState.legal.processosImageUrl}
                              onChange={handleLegalLinkChange}
                              disabled={!canEdit}
                            />
                          </div>
                          <div className="ces-field">
                            <Label
                              value="Recência da restrição"
                              className="text-xs font-medium text-slate-700"
                            />
                            <Select
                              value={formState.recency.processosLesivos}
                              onChange={handleRecencyChange("processosLesivos")}
                              disabled={
                                !canEdit || !formState.legal.processosLesivos
                              }
                            >
                              {RECENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </RuleCheckbox>
                    </div>
                  </section>
                )}

                {isLastStep && (
                  <section className="ces-card">
                    <div className="ces-card-header">
                      <div>
                        <h2>Resumo e observações</h2>
                        <p className="text-sm text-slate-500">
                          Veja como o motor aplica cada regra de reprovação
                          automática.
                        </p>
                      </div>
                    </div>
                    <div className="ces-card-body space-y-5">
                      <div className="ces-summary-grid">
                        <p className="ces-summary-label">Situação atual</p>
                        {visibleSummaryItems.length === 0 ? (
                          <p className="ces-summary-empty">
                            Nenhuma regra configurada ainda.
                          </p>
                        ) : (
                          <ul className="ces-summary-list">
                            {visibleSummaryItems.map((item) => (
                              <SummaryItem
                                key={item.id}
                                active={true}
                                text={item.text}
                              />
                            ))}
                          </ul>
                        )}
                      </div>
                    <div className="ces-summary-section">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-900">
                          {editingPresetId
                            ? "Nome do template (edição)"
                            : "Nome do template"}
                        </h3>
                        <span className="text-sm text-red-500">
                          Obrigatório
                        </span>
                      </div>
                        <TextInput
                          id="creditEngineTemplateName"
                          type="text"
                          placeholder="Ex: Motor PJ conservador"
                          value={templateName}
                          onChange={(event) =>
                            setTemplateName(event.target.value)
                          }
                          required
                          disabled={!canEdit}
                        />
                        <p className="ces-template-helper">
                          Esse nome identifica a configuração salva para uso
                          futuro.
                        </p>
                      </div>
                      <div className="ces-summary-section">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Observações internas
                          </h3>
                          <span className="text-sm">Opcional</span>
                        </div>
                        <Textarea
                          rows={4}
                          value={formState.notes}
                          onChange={handleNotesChange}
                          placeholder="Use este campo para juntar instruções, exceções ou contexto adicional."
                          disabled={!canEdit}
                        />
                      </div>

                      <div className="ces-summary-section">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Consulta base
                          </h3>
                          <span className="text-sm text-red-500">
                            Obrigatório
                          </span>
                        </div>
                        <div className="ces-query-summary">
                          <div>
                            <p className="ces-query-name">{queryLabel}</p>
                            <p className="ces-template-helper">
                              {selectedQuery?.url ||
                                "Selecione a consulta base no passo anterior."}
                            </p>
                          </div>
                          <Button
                            type="button"
                            color="light"
                            onClick={() => setWizardStep(1)}
                          >
                            Alterar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {alert && (
                  <Alert
                    className="ces-alert"
                    color={
                      alert.type === "error"
                        ? "failure"
                        : alert.type === "success"
                        ? "success"
                        : alert.type === "warning"
                        ? "warning"
                        : "info"
                    }
                    icon={
                      alert.type === "success"
                        ? HiOutlineShieldCheck
                        : HiOutlineExclamationCircle
                    }
                    onDismiss={() => setAlert(null)}
                  >
                    {alert.message}
                  </Alert>
                )}

                {isRulesStep && (
                  <div className="ces-actions-bar ces-actions-bar--wizard">
                    <Button
                      type="button"
                      color="light"
                      onClick={() => setWizardStep(wizardStep - 1)}
                      disabled={wizardStep === 1}
                    >
                      Voltar
                    </Button>
                    {!isLastStep && (
                      <Button
                        type="button"
                        onClick={() => setWizardStep(wizardStep + 1)}
                        disabled={!selectedQueryId}
                      >
                        Próximo
                      </Button>
                    )}
                    {isLastStep && (
                      <Button
                        type="submit"
                        disabled={
                          savingConfig ||
                          !canEdit ||
                          templateName.trim() === "" ||
                          !selectedQueryId
                        }
                      >
                        {savingConfig ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Salvando...
                          </>
                        ) : (
                          "Salvar configurações"
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function RuleCheckbox({
  label,
  description,
  checked,
  onChange,
  disabled,
  children,
}) {
  return (
    <label
      className={cx(
        "flex flex-col gap-3 rounded-xl border p-3 transition-all cursor-pointer",
        checked
          ? "border-emerald-500/70 bg-emerald-50/70 shadow-sm"
          : "border-slate-300 bg-white hover:border-emerald-300/80 hover:bg-emerald-50/30",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="mt-0.5"
        />
        <div>
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>

      {children && (
        <div
          className={cx(
            "ces-rule-body px-1",
            !checked && "ces-rule-body--disabled"
          )}
        >
          {children}
        </div>
      )}
    </label>
  );
}

function SummaryItem({ active, text }) {
  return (
    <li className="flex items-center gap-2 text-sm text-slate-600">
      <span
        className={cx(
          "h-2 w-2 rounded-full",
          active ? "bg-emerald-500" : "bg-slate-300"
        )}
      />
      <span>{text}</span>
    </li>
  );
}
